import { TAG_IDS } from "@/state/api";

/**
 * Tag helpers for every RTK Query endpoint, in both apps (GEN5-5269 DEDUPE-2
 * §5: this file replaces `common/utilities/rtkToolkit.js` and admin's copy).
 * See https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#abstracting-common-providesinvalidates-usage
 */

export type TagId = string | number;

/** A tag RTK Query provides or invalidates: a type, and the record (or `LIST`) it is for. */
export type Tag<TType extends string> = { type: TType; id: TagId };

export type TagsListOptions = {
  /**
   * Still give the list tag when the request failed, so that a later
   * invalidation of the list retries it (RTK's own recipe). Off: a failed
   * request gives no tag at all.
   */
  listOnError?: boolean;
  /** A detail response (`data` is one record) also gives that record's tag. */
  detail?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const tagIdOf = (item: unknown): TagId | undefined => {
  if (!isRecord(item)) return undefined;
  const { id } = item;
  return typeof id === "string" || typeof id === "number" ? id : undefined;
};

/**
 * Create a tag that has an abstract id representing a list of items.
 * @param type The tag type
 */
export function createListTag<TType extends string>(type: TType) {
  return { type, id: TAG_IDS.LIST } as const;
}

/**
 * Tags for a `{ data }` response: the list tag, and one tag per item of
 * `data` that has an id (or, with `detail`, for the one record `data` is).
 * Used as `providesTags`, and as `invalidatesTags` on a mutation whose
 * response is the record it changed.
 */
export function providesTagsList<TType extends string>(
  type: TType,
  { listOnError = false, detail = false }: TagsListOptions = {}
) {
  // RTK passes (result, error, arg); the arg is not read.
  return (results: unknown, error?: unknown, _arg?: unknown): Tag<TType>[] => {
    if (error !== undefined && error !== null) {
      return listOnError ? [createListTag(type)] : [];
    }
    const data = isRecord(results) ? results["data"] : undefined;
    const items = Array.isArray(data) ? data : detail ? [data] : [];
    const itemTags = items.flatMap((item): Tag<TType>[] => {
      const id = tagIdOf(item);
      return id === undefined ? [] : [{ type, id }];
    });
    return [createListTag(type), ...itemTags];
  };
}
